import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Button, Card, Typography, Spin, Alert } from 'antd';
import { 
  UserOutlined, 
  LockOutlined, 
  SafetyCertificateOutlined, 
  BankOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import '../AuthPage.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Institute authentication page - Login only
 */
const InstituteAuthPage = () => {
  const { user, isAuthenticated, isLoading, error, login, clearError, setError } = useAuth();
  const history = useHistory();
  const location = useLocation();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const nextUrl = new URLSearchParams(location.search).get('next') || '/admin';
      history.replace(nextUrl);
    }
  }, [isAuthenticated, user, history, location]);

  // Handle callback errors
  useEffect(() => {
    const error = new URLSearchParams(location.search).get('error');
    if (error) {
      let errorMessage = 'Authentication failed. Please try again.';
      
      switch (error) {
        case 'missing_tokens':
          errorMessage = 'Authentication tokens were not received. Please try again.';
          break;
        case 'session_validation_failed':
          errorMessage = 'Session validation failed. Please try again.';
          break;
        case 'session_validation_error':
          errorMessage = 'Error validating your session. Please try again.';
          break;
        case 'callback_error':
          errorMessage = 'Error during authentication. Please try again.';
          break;
        default:
          errorMessage = `Authentication error: ${error}. Please try again.`;
      }
      
      clearError();
      setTimeout(() => {
        setError(errorMessage);
      }, 100);
    }
  }, [location, clearError, setError]);

  // Handle OIDC login with institute role context
  const handleOIDCLogin = () => {
    clearError();
    login("institute");
  };

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="auth-loading">
        <Spin size="large" />
        <div className="auth-loading-text">
          <Text>Verifying your session...</Text>
        </div>
      </div>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <div className="auth-container">
        <Card className="auth-card">
          <div className="auth-content">
            <div className="auth-header">
              <SafetyCertificateOutlined className="auth-icon error" />
              <Title level={2} className="auth-title">
                Authentication Error
              </Title>
            </div>
            
            <Alert
              message="Login Failed"
              description={error}
              type="error"
              showIcon
              className="auth-error"
            />
            
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button"
              icon={<UserOutlined />}
            >
              Try Again
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Main institute login page
  return (
    <div className="auth-container">
      <Card className="auth-card onboarding-card">
        <div className="auth-content">
          {/* Header */}
          <div className="auth-header">
            <BankOutlined className="auth-icon institute-icon" />
            <Title level={1} className="auth-title">
              Institute Portal
            </Title>
            <Text className="auth-subtitle">
              Access your educational institution&apos;s management dashboard
            </Text>
          </div>

          {/* Benefits */}
          <div className="onboarding-benefits">
            <Title level={4}>Institute Management Features:</Title>
            <div className="benefits-list">
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Student enrollment and management</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Course and curriculum oversight</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Faculty and staff administration</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Academic progress tracking</Text>
              </div>
              <div className="benefit-item">
                <CheckCircleOutlined className="benefit-icon" />
                <Text>Financial and resource management</Text>
              </div>
            </div>
          </div>

          {/* Auth Actions */}
          <div className="auth-actions">
            <Button 
              type="primary" 
              size="large" 
              onClick={handleOIDCLogin}
              className="auth-button oidc-button primary-cta"
              icon={<LockOutlined />}
              loading={isLoading}
            >
              Sign In to Institute Portal
            </Button>
          </div>

          {/* Security Info */}
          <div className="auth-info-section">
            <Paragraph className="auth-info">
              <SafetyCertificateOutlined /> Secure access to your institution&apos;s data
            </Paragraph>
            <Paragraph className="auth-info small">
              Your institutional information is protected with enterprise-grade security
            </Paragraph>
          </div>

          {/* Footer */}
          <div className="auth-footer">
            <Text type="secondary">
              Need access to the institute portal? <a href="mailto:institute-support@strideahead.com">Contact our team</a>
            </Text>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default InstituteAuthPage;
